/**
 * Messaging redesign (Sept 2026, "2a — PORTAL, SOFTENED"): the numbers and the pure logic.
 * Handoff: docs/plans/2026-09-messaging-redesign/README.md.
 *
 * Nothing in here touches the DOM. The motion helpers in messagesMotion.js read these
 * constants; the components read the tokens; the tests read all of it.
 */
import { formatDuration } from '../VoiceRecorder';
import { EASE_FORWARD, EASE_BACK, HOLE_RATIO } from '../transitions/portalMotion';

// ── Tokens ────────────────────────────────────────────────────────────────────────────

export const T = {
  white: '#FFFFFF',
  canvas: '#F5F5F5',
  off: '#F2F2F2',
  dayPill: '#E5E7EB',
  ink: '#000000',
  mine: '#A8D8EA',          // Nintendo blue
  mineTime: '#28405A',
  grey400: '#9CA3AF',
  grey500: '#6B7280',
  grey600: '#4B5563',
  live: '#FF00AA',
  beam: '#00FFFF',
  danger: '#EF4444',
  activeBadge: '#FFCCE6',
};

export const RADIUS = {
  pill: 999,
  modal: 28,
  allReactions: 24,
  bubble: 20,
  row: 18,
  photo: 14,
  quote: 14,
  thumb: 12,
  small: 9,
};

/** The message-body face. Chrome is JetBrains Mono (the page's default). */
export const BODY_FONT = 'Arial, sans-serif';
/**
 * The size of the words in a bubble and in the composer — Instagram's: 17px on a phone
 * (which is also the size under which iOS zooms a focused field), 15px with a mouse.
 */
export const BODY_SIZE = { phone: 17, desktop: 15 };
export const bodySize = (phone) => (phone ? BODY_SIZE.phone : BODY_SIZE.desktop);
/**
 * How much of the thread's width one bubble may take. On a phone the old 68% broke a
 * sentence every four or five words — WhatsApp gives a bubble about three quarters of
 * the screen, and 80% of the row (the avatar gutter is inside it) is the same line.
 * The desktop pane is wide enough that 68% is already a long line, so it is unchanged.
 */
export const BUBBLE_MAX = { phone: '80%', desktop: '68%' };
export const bubbleMax = (phone) => (phone ? BUBBLE_MAX.phone : BUBBLE_MAX.desktop);
export const EMOJI_FONT = "'Apple Color Emoji','Segoe UI Emoji','Noto Color Emoji',sans-serif";

// ── Easings (portalMotion owns EF/EB; the rest are the handoff's) ─────────────────────

export const EASE = {
  EF: EASE_FORWARD,
  EB: EASE_BACK,
  OUT: 'cubic-bezier(0, 0, 0.2, 1)',
  IN: 'cubic-bezier(0.4, 0, 1, 1)',
  SMOOTH: 'cubic-bezier(.2,.9,.3,1)',
  SLIDE: 'cubic-bezier(.2,.7,.2,1)',
};

export const PORTAL_MS = 640;
export const PORTAL_RING_PX = 56;
export const REDUCED_MS = 160;
/** The ring's band centre, as a fraction of its radius. The clip edge hides under it. */
export const BAND_RATIO = 0.94;
export const OLD_SCALE = 0.92;
export const NEW_SCALE = 1.14;
export const DIM = 0.55;

// ── Reactions ─────────────────────────────────────────────────────────────────────────

export const QUICK_REACTIONS = ['👍', '😂', '🔥', '❤️', '👀', '🙏'];
export const ALL_REACTIONS = [
  '👍', '👎', '😂', '🤣', '😭', '😩', '😮', '😍', '🥹', '😅', '😬', '🙃', '😎', '🤔', '🫠', '😴',
  '❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '🔥', '✨', '🎉', '🥳', '🪩', '🍸', '🍷', '🍻',
  '🙏', '👏', '🙌', '💪', '🤝', '👀', '💯', '✅', '☀️', '🌙', '🌊', '⛵', '🧘', '🎶', '🎤', '🕺',
];

// ── Formatting ────────────────────────────────────────────────────────────────────────

export function formatTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).toUpperCase();
}

export function formatDay(iso) {
  const d = new Date(iso);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return 'TODAY';
  const yesterday = new Date(today.getTime() - 86400000);
  if (d.toDateString() === yesterday.toDateString()) return 'YESTERDAY';
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }).toUpperCase();
}

export function formatBytes(n) {
  if (!n) return '';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + ' MB';
  if (n >= 1_000) return Math.round(n / 1_000) + ' KB';
  return n + ' B';
}

/**
 * Where a view-once message stands, for this viewer.
 *
 * The server sends facts (who opened it, whether it's been wiped); the state is a question
 * of who is asking. The sender never opens: theirs reads sent → opened. A recipient's reads
 * sealed → opened, or expired if it was wiped before they looked.
 */
export function viewOnceState(msg, viewerUserId) {
  const v = msg?.viewOnce;
  if (!v) return null;
  const opened = v.openedBy || [];
  if (msg.senderId === viewerUserId) return opened.length ? 'opened' : 'sent';
  if (viewsLeft(msg, viewerUserId) <= 0) return 'opened';
  if (v.wiped) return 'expired';
  return opened.some((o) => o.userId === viewerUserId) ? 'replay' : 'sealed';
}

/** Looks this viewer still has: the limit less their own entries in the open list. */
export function viewsLeft(msg, viewerUserId) {
  const v = msg?.viewOnce;
  if (!v) return 0;
  const used = (v.openedBy || []).filter((o) => o.userId === viewerUserId).length;
  return Math.max(0, (v.viewLimit || 1) - used);
}

/** The three settings the composer offers, in the order they are listed. */
export const VIEW_LIMITS = [
  { value: 1, label: 'VIEW ONCE' },
  { value: 2, label: 'VIEW TWICE' },
  { value: 0, label: 'UNLIMITED VIEWS' },
];
export const viewLimitLabel = (limit) => (VIEW_LIMITS.find((o) => o.value === (limit || 0)) || VIEW_LIMITS[2]).label;

/** The sealed bubble's one line: what it is, and what has become of it. */
export function viewOnceLabel(msg, state) {
  const what = msg.kind === 'image' ? 'PHOTO' : msg.kind === 'video' ? 'VIDEO' : msg.kind === 'album' ? `${msg.albumCount || msg.albumItems?.length || 0} ITEMS` : 'MESSAGE';
  const how = (msg.viewOnce?.viewLimit || 1) > 1 ? 'VIEW TWICE' : 'VIEW ONCE';
  switch (state) {
    case 'sent': return `${how} · ${what} · SENT`;
    case 'opened': {
      const names = [...new Set((msg.viewOnce?.openedBy || []).map((o) => o.userId))];
      return names.length > 1 ? `OPENED BY ${names.length}` : 'OPENED';
    }
    case 'replay': return `${what} · 1 LEFT`;
    case 'expired': return `${how} · ${what} · EXPIRED`;
    default: return `${how} · ${what}`;
  }
}

/**
 * "Seen 1m ago" — the line under the last of your messages the other side has opened the
 * thread after. The receipt is the moment they last opened the thread, so it reads as a
 * time, not a tick: just now, minutes, hours, then the day.
 */
export function seenLabel(at, now = Date.now()) {
  if (!at) return '';
  const ms = now - new Date(at).getTime();
  if (ms < 60_000) return 'Seen just now';
  if (ms < 3_600_000) return `Seen ${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `Seen ${Math.floor(ms / 3_600_000)}h ago`;
  return `Seen ${formatDay(at)}`;
}

/**
 * Which of the viewer's messages carries the seen line, and what it says.
 *
 * One line, under the LAST message of mine that was sent before the other side last opened
 * the thread — like Instagram, not a tick on every bubble. A 1:1 names the moment; a group
 * counts the members who have been in since.
 */
export function seenReceipt(messages, reads, viewerUserId, { isGroup = false, now = Date.now() } = {}) {
  if (!reads?.length || !messages?.length) return null;
  const mine = messages.filter((m) => m.senderId === viewerUserId && !m.system && !m._outbox);
  for (let i = mine.length - 1; i >= 0; i -= 1) {
    const sentAt = new Date(mine[i].createdAt).getTime();
    const seenBy = reads.filter((r) => r.userId !== viewerUserId && new Date(r.lastReadAt).getTime() >= sentAt);
    if (seenBy.length) {
      if (isGroup) return { messageId: mine[i].id, label: `Seen by ${seenBy.length}` };
      const latest = seenBy.reduce((a, r) => (new Date(r.lastReadAt) > new Date(a.lastReadAt) ? r : a));
      return { messageId: mine[i].id, label: seenLabel(latest.lastReadAt, now) };
    }
  }
  return null;
}

/** How long a video message plays: the trim window if it has one, else the whole clip. */
export function clipSeconds(msg) {
  if (!msg) return 0;
  if (msg.videoTrimEndMs > msg.videoTrimStartMs) return Math.round((msg.videoTrimEndMs - msg.videoTrimStartMs) / 1000);
  return Math.round(msg.videoDurationSec || 0);
}

export function previewText(msg) {
  if (!msg) return '';
  if (msg.viewOnce) return msg.kind === 'image' ? '[ VIEW ONCE · PHOTO ]' : msg.kind === 'video' ? '[ VIEW ONCE · VIDEO ]' : msg.kind === 'album' ? `[ VIEW ONCE · ${msg.albumCount || 0} ITEMS ]` : '[ VIEW ONCE ]';
  if (msg.kind === 'image') return `[ IMAGE${msg.imageSizeBytes ? ' · ' + formatBytes(msg.imageSizeBytes) : ''} ]`;
  if (msg.kind === 'album') return `[ ALBUM · ${msg.albumCount || msg.albumItems?.length || 0} ]`;
  if (msg.kind === 'call') return callPreview(msg);
  if (msg.kind === 'gif') return `[ GIF ]${msg.gifLabel ? ' ' + msg.gifLabel : ''}`;
  if (msg.kind === 'audio') return `[ VOICE NOTE${msg.audioDurationSec ? ' · ' + formatDuration(msg.audioDurationSec) : ''} ]`;
  if (msg.kind === 'video') return `[ VIDEO${clipSeconds(msg) ? ' · ' + formatDuration(clipSeconds(msg)) : ''} ]`;
  if (msg.kind === 'link') return msg.linkTitle || msg.linkUrl || '[ LINK ]';
  if (msg.kind === 'plan') return `[ ${msg.plan?.kind === 'event' ? 'EVENT' : 'PORTAL'} ] ${msg.plan?.title || msg.body || ''}`.trim();
  return msg.body || '';
}

// ── Calls (Oct 2026, Part B) ──────────────────────────────────────────────────────────

/** `4:32` */
export function callDuration(sec) {
  const s = Math.max(0, Math.round(sec || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * How a call record reads to this viewer. One row per call, posted by the caller; the reader's
 * side decides the words: a no-answer is NO ANSWER to the caller and MISSED to the callee.
 *   { label, sub, missed, callBack }
 */
export function callRecordView(msg, viewerUserId, { peerName = 'THEM' } = {}) {
  const c = msg.call || {};
  const kind = c.video ? 'VIDEO' : 'VOICE';
  const caller = msg.senderId === viewerUserId;
  const who = (peerName || 'THEM').toUpperCase();
  const group = (c.people || 2) > 2;
  switch (c.status) {
    case 'noanswer':
      return caller
        ? { label: 'NO ANSWER', sub: `${kind} CALL`, missed: false, callBack: true }
        : { label: `MISSED ${kind} CALL`, sub: `${who} CALLED`, missed: true, callBack: true };
    case 'cancelled':
      return caller
        ? { label: `CANCELLED ${kind} CALL`, sub: 'YOU HUNG UP', missed: false, callBack: false }
        : { label: `MISSED ${kind} CALL`, sub: `${who} CALLED`, missed: true, callBack: true };
    case 'declined':
      return caller
        ? { label: `DECLINED ${kind} CALL`, sub: `${who} DECLINED`, missed: false, callBack: true }
        : { label: `DECLINED ${kind} CALL`, sub: `${who} CALLED`, missed: false, callBack: true };
    default:
      return { label: `${group ? 'GROUP ' : ''}${kind} CALL`, sub: `${callDuration(c.durationSec)}${group ? ` · ${c.people} PEOPLE` : ''}`, missed: false, callBack: false };
  }
}

/** The inbox line for a call record: `[ VOICE CALL · 4:32 ]` / `[ MISSED CALL ]`. */
export function callPreview(msg) {
  const c = msg.call || {};
  const kind = c.video ? 'VIDEO' : 'VOICE';
  if (c.status === 'done') return `[ ${kind} CALL · ${callDuration(c.durationSec)} ]`;
  if (c.status === 'declined') return `[ DECLINED ${kind} CALL ]`;
  return '[ MISSED CALL ]';
}

/** The first name, upper-cased, the way it sits over a run. */
export function firstNameOf(displayName) {
  return ((displayName || '').split(' ')[0] || displayName || '').toUpperCase();
}

/** The Portal card's room line: "FRIENDS · 6 IN". */
export function portalRoomLabel(portal) {
  if (!portal) return '';
  const room = (portal.room || 'friends').replace('_', ' ').toUpperCase();
  const n = portal.acceptedCount || 0;
  return n > 0 ? `${room} · ${n} IN` : room;
}

/** The Portal card's status pill: `LIVE · 2H LEFT` while it's up, else `EXPIRED`. */
export function portalStatus(portal, now = Date.now()) {
  if (!portal) return { label: 'EXPIRED', live: false };
  const exp = portal.expiresAtUtc ? new Date(portal.expiresAtUtc).getTime() : null;
  const live = !!portal.isLive && (exp == null || exp > now);
  if (!live) return { label: 'EXPIRED', live: false };
  if (exp == null) return { label: 'LIVE', live: true };
  const mins = Math.max(1, Math.floor((exp - now) / 60000));
  const h = Math.floor(mins / 60);
  const left = h > 0 ? `${h}H` : `${mins}M`;
  return { label: `LIVE · ${left} LEFT`, live: true };
}

// ── Runs ──────────────────────────────────────────────────────────────────────────────

/**
 * Consecutive messages from one sender form a run: 3px between them, 8px between runs.
 * The sender's name shows once above the first bubble; their avatar once beside the last.
 * A system line breaks a run on both sides. Days are grouped first (the caller pins a
 * day pill over each group), then runs inside a day.
 *
 * Returns [{ day, items: [{ msg, mine, firstOfRun, lastOfRun }] }].
 */
export function groupMessages(messages, { viewerUserId, peerUserId } = {}) {
  const groups = [];
  let day = null;
  const isMine = (m) => (viewerUserId ? m.senderId === viewerUserId : m.senderId !== peerUserId);
  for (const m of messages) {
    const d = formatDay(m.createdAt);
    if (d !== day) { groups.push({ day: d, items: [] }); day = d; }
    groups[groups.length - 1].items.push({ msg: m, mine: !!m.system ? false : isMine(m), firstOfRun: true, lastOfRun: true });
  }
  for (const g of groups) {
    const items = g.items;
    for (let i = 0; i < items.length; i++) {
      const cur = items[i];
      const prev = items[i - 1];
      const next = items[i + 1];
      const joins = (a, b) => a && b && !a.msg.system && !b.msg.system && a.msg.senderId === b.msg.senderId;
      cur.firstOfRun = !joins(prev, cur);
      cur.lastOfRun = !joins(cur, next);
    }
  }
  return groups;
}

/** The bubble's corners: 20 all round, with a 6px tail on the last bubble of a run. */
export function bubbleRadius({ mine, lastOfRun }) {
  const r = RADIUS.bubble;
  if (!lastOfRun) return `${r}px`;
  return mine ? `${r}px ${r}px 6px ${r}px` : `${r}px ${r}px ${r}px 6px`;
}

// ── The doorway ───────────────────────────────────────────────────────────────────────

/** A cubic-bezier as a function of progress, so the ring and the clip can share one clock. */
export function cubicBezier(x1, y1, x2, y2) {
  const cx = 3 * x1; const bx = 3 * (x2 - x1) - cx; const ax = 1 - cx - bx;
  const cy = 3 * y1; const by = 3 * (y2 - y1) - cy; const ay = 1 - cy - by;
  const X = (t) => ((ax * t + bx) * t + cx) * t;
  const Y = (t) => ((ay * t + by) * t + cy) * t;
  const dX = (t) => (3 * ax * t + 2 * bx) * t + cx;
  return (x) => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    let t = x;
    for (let i = 0; i < 8; i++) {
      const d = dX(t);
      if (Math.abs(d) < 1e-6) break;
      t -= (X(t) - x) / d;
    }
    if (t < 0 || t > 1 || Math.abs(X(t) - x) > 1e-4) {
      let lo = 0; let hi = 1; t = x;
      for (let i = 0; i < 30; i++) { if (X(t) < x) lo = t; else hi = t; t = (lo + hi) / 2; }
    }
    return Y(t);
  };
}

export const EASE_POINTS = {
  EF: [0.55, 0, 0.7, 0.2],
  EB: [0.3, 0.8, 0.45, 1],
};

/** Farthest-corner distance from the origin, with the handoff's 4% margin. */
export function coverRadius(origin, box) {
  return Math.hypot(Math.max(origin.x, box.width - origin.x), Math.max(origin.y, box.height - origin.y)) * 1.04;
}

/**
 * The sampled clock behind a Portal transition. `origin` is inside `box`; `doorway` means
 * the ring starts at scale 1 exactly over a tapped ring instead of at 0.2.
 *
 * Every frame: the ring's scale, the incoming layer's scale, the clip radius on that layer
 * (compensated for its own scale so the visible edge stays on the band), the dim's opacity
 * and the outgoing layer's scale — all from the same eased progress.
 */
export function portalKeyframes({ origin, box, back = false, doorway = false, ringPx = PORTAL_RING_PX, samples = 28 }) {
  const band = (ringPx / 2) * BAND_RATIO;
  const R = coverRadius(origin, box);
  const kS = R / band;
  const k0 = doorway ? 1 : 0.2;
  const ease = cubicBezier(...(back ? EASE_POINTS.EB : EASE_POINTS.EF));
  const frames = [];
  for (let i = 0; i <= samples; i++) {
    const p = ease(i / samples);
    const ringScale = back ? kS + (0.2 - kS) * p : k0 + (kS - k0) * p;
    const layerScale = back ? 1 + (NEW_SCALE - 1) * p : NEW_SCALE + (1 - NEW_SCALE) * p;
    const clip = Math.max(0, (band * ringScale) / layerScale);
    const dim = back ? DIM * (1 - p) : DIM * p;
    const under = back ? OLD_SCALE + (1 - OLD_SCALE) * p : 1 + (OLD_SCALE - 1) * p;
    frames.push({ p, ringScale, layerScale, clip, dim, under });
  }
  return { frames, R, kS, k0, band, hole: (ringPx / 2) * HOLE_RATIO };
}
